import { ImageIcon, Star } from "lucide-react";

import {
  type CollectionPreviewGroup,
  type CollectionPreviewResponse,
  type CollectionPreviewRestaurant,
  type RestaurantsListCollectionPreview as RestaurantsListPreviewData,
  type SelectableGroupCollectionPreview as SelectableGroupPreviewData,
} from "@/api/admin/collections";
import { CollectionType, type MediaItemDto } from "@/api/admin/types";
import { PaginationNext, PaginationPrevious } from "@/components/atoms";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
} from "@/components/ui/pagination";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface CollectionPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: CollectionPreviewResponse | null;
  isPagePending: boolean;
  onOffsetChange: (offset: number) => void;
}

const isImage = (type: string) => type.startsWith("image/");

const Media = ({
  media,
  alt,
  className,
}: {
  media?: MediaItemDto | null;
  alt: string;
  className: string;
}) => {
  if (!media) {
    return (
      <div
        className={`${className} flex items-center justify-center bg-zinc-800`}
      >
        <ImageIcon className="size-8 text-zinc-500" />
      </div>
    );
  }

  if (isImage(media.type)) {
    return (
      <img src={media.url} alt={alt} className={`${className} object-cover`} />
    );
  }

  return (
    <video className={`${className} object-cover`} muted playsInline>
      <source src={media.url} type={media.type} />
    </video>
  );
};

const formatDistance = (restaurant: CollectionPreviewRestaurant) => {
  if (restaurant.eta) {
    return `${(restaurant.eta.distanceMeters / 1000).toFixed(1)} km`;
  }
  if (restaurant.distance != null) {
    return `${restaurant.distance.toFixed(1)} km`;
  }
  return null;
};

const RestaurantCard = ({ item }: { item: CollectionPreviewRestaurant }) => {
  const media = item.mediaItems?.[0];
  const details = item.cuisines.map(cuisine => cuisine.value).join(", ");
  const distance = formatDistance(item);

  return (
    <article className="w-[56%] min-w-46 shrink-0 snap-start">
      <div className="relative mb-2 overflow-hidden rounded-xl bg-zinc-800">
        <Media media={media} alt={item.name} className="aspect-square w-full" />
        {item.brandLogo && (
          <img
            src={item.brandLogo}
            alt=""
            className="absolute bottom-3 left-3 size-12 rounded-xl border border-white/10 bg-black object-contain p-1"
          />
        )}
      </div>
      <h4 className="truncate text-sm font-semibold text-white">{item.name}</h4>
      {details && (
        <p className="mt-1 truncate text-xs text-zinc-400">{details}</p>
      )}
      {(item.rating != null || distance) && (
        <p className="mt-2 flex items-center gap-1 text-xs text-zinc-400">
          {item.rating != null && (
            <>
              <Star className="size-3 fill-amber-300 text-amber-300" />
              {item.rating.toFixed(1)}
            </>
          )}
          {item.rating != null && distance && <span>·</span>}
          {distance}
        </p>
      )}
    </article>
  );
};

const GroupCard = ({ group }: { group: CollectionPreviewGroup }) => (
  <article className="flex min-h-28 w-[31%] min-w-26 shrink-0 snap-start flex-col items-center justify-center rounded-xl bg-zinc-800 p-3 text-center">
    {group.iconUrl ? (
      <img src={group.iconUrl} alt="" className="mb-2 size-7 object-contain" />
    ) : (
      <span className="mb-2 text-xl font-bold text-zinc-300">
        {group.name.charAt(0)}
      </span>
    )}
    <h4 className="text-xs font-medium text-white">{group.name}</h4>
  </article>
);

const RestaurantsListPreview = ({
  preview,
}: {
  preview: RestaurantsListPreviewData;
}) => (
  <div className="m-3 rounded-[22px] bg-zinc-900 p-5">
    <h3 className="text-2xl font-bold text-white">{preview.title}</h3>
    {preview.subtitle && (
      <p className="mt-1 text-sm text-zinc-400">{preview.subtitle}</p>
    )}
    <div className="hide-scrollbar mt-5 flex snap-x snap-mandatory gap-3 overflow-x-auto pb-2">
      {preview.items.map(item => (
        <RestaurantCard key={item.id} item={item} />
      ))}
    </div>
  </div>
);

const SelectableGroupPreview = ({
  preview,
  uploadedMedia,
}: {
  preview: SelectableGroupPreviewData;
  uploadedMedia: MediaItemDto | null;
}) => (
  <div className="m-3 overflow-hidden rounded-[22px] bg-zinc-900">
    <div className="relative overflow-hidden">
      <Media
        media={uploadedMedia}
        alt={preview.title}
        className="aspect-[6/5] w-full"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5">
        <h3 className="text-2xl font-bold text-white">{preview.title}</h3>
        {preview.subtitle && (
          <p className="mt-1 text-sm text-zinc-200">{preview.subtitle}</p>
        )}
      </div>
    </div>
    <div className="hide-scrollbar flex snap-x snap-mandatory gap-3 overflow-x-auto p-4">
      {preview.groups.map(group => (
        <GroupCard
          key={`${group.filterId}-${String(group.filterValue)}`}
          group={group}
        />
      ))}
    </div>
  </div>
);

const MobilePreview = ({ data }: { data: CollectionPreviewResponse }) => {
  const preview = data.preview;

  if (!preview) {
    return (
      <div className="flex min-h-72 items-center justify-center px-8 text-center text-sm text-zinc-400">
        No collection content matches the selected filters.
      </div>
    );
  }

  if (preview.type === CollectionType.RESTUARANTS_LIST) {
    return <RestaurantsListPreview preview={preview} />;
  }

  return (
    <SelectableGroupPreview
      preview={preview}
      uploadedMedia={data.uploadedMedia ?? preview.mediaItem}
    />
  );
};

interface MatchingBranchesProps {
  data: CollectionPreviewResponse;
  isPagePending: boolean;
  onOffsetChange: (offset: number) => void;
}

export const MatchingBranches = ({
  data,
  isPagePending,
  onOffsetChange,
}: MatchingBranchesProps) => {
  const isPreviousDisabled = data.offset === 0 || isPagePending;
  const isNextDisabled = !data.hasMore || isPagePending;

  return (
    <section className="flex min-h-0 flex-col border-b lg:border-r lg:border-b-0">
      <div className="flex items-center gap-3 border-b px-6 py-4">
        <h3 className="text-sm font-semibold">Matching branches</h3>
        <span className="rounded-full bg-black px-3 py-1 text-xs font-semibold text-white">
          {data.total} {data.total === 1 ? "branch" : "branches"}
        </span>
      </div>
      <ScrollArea className="min-h-56 flex-1">
        {data.branches.length > 0 ? (
          <ol className="grid grid-cols-1 gap-x-7 px-6 py-2 sm:grid-cols-2">
            {data.branches.map((branch, index) => (
              <li
                key={branch.id}
                className="flex min-w-0 gap-3 border-b py-3 text-sm"
              >
                <span className="shrink-0 text-zinc-400">
                  {String(data.offset + index + 1).padStart(2, "0")}
                </span>
                <span className="truncate font-medium">{branch.name}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="px-6 py-10 text-center text-sm text-zinc-500">
            No matching branches found.
          </p>
        )}
      </ScrollArea>
      {data.total > 0 && (
        <div className="flex items-center justify-between border-t px-6 py-4">
          <span className="text-sm text-zinc-500">
            {data.offset + 1}–
            {Math.min(data.offset + data.branches.length, data.total)} of{" "}
            {data.total}
          </span>
          <Pagination className="mx-0 w-auto justify-end">
            <PaginationContent className="gap-2">
              <PaginationItem>
                <PaginationPrevious
                  aria-disabled={isPreviousDisabled}
                  className={
                    isPreviousDisabled
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                  size="md"
                  onClick={() =>
                    onOffsetChange(Math.max(0, data.offset - data.limit))
                  }
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  aria-disabled={isNextDisabled}
                  className={
                    isNextDisabled
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                  size="md"
                  onClick={() => onOffsetChange(data.offset + data.limit)}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </section>
  );
};

export const CollectionPreview = ({
  open,
  onOpenChange,
  data,
  isPagePending,
  onOffsetChange,
}: CollectionPreviewProps) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent className="flex h-full w-[min(1100px,95vw)] max-w-none! flex-col gap-0 p-0 sm:max-w-none!">
      <SheetHeader className="shrink-0 border-b px-6 py-5">
        <SheetTitle>Collection Preview</SheetTitle>
        <SheetDescription className="sr-only">
          Matching branches and an English mobile collection preview
        </SheetDescription>
      </SheetHeader>

      {data && (
        <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[minmax(360px,1fr)_480px]">
          <MatchingBranches
            data={data}
            isPagePending={isPagePending}
            onOffsetChange={onOffsetChange}
          />

          <section className="min-h-0 overflow-y-auto bg-zinc-50 p-5 sm:p-8">
            <div className="mx-auto w-full max-w-[400px] overflow-hidden rounded-[34px] border-[12px] border-zinc-950 bg-zinc-950 shadow-xl">
              <MobilePreview data={data} />
            </div>
          </section>
        </div>
      )}
    </SheetContent>
  </Sheet>
);
